import React, { useMemo, useState, useCallback } from 'react';
import exerciseRowStyles from './ExerciseRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import ExerciseRow from './ExerciseRow';
import ListContent from '../composites/ListContent';
import BaseExerciseDialog from './BaseExerciseDialog';
import { isMotivisionTrainer } from '../../utils/dashboardUtils';
import { useSessionData, useUserAction } from '../../providers';

import type { Exercise, WithOptionalId } from '../../types';

const exerciseListTitles = [
  'Exercise Name',
  'Equipment Type',
  'Muscle Group',
  'Movement Pattern',
  'Created By',
  'Options',
];

export interface AddExerciseModalData {
  baseExercise: Exercise | null;
  isCreatingPersonalizedExercise?: boolean;
}

const TrainerExercisesContent: React.FC = () => {
  const { updateExercise, createExercise, deleteUserExercise } =
    useUserAction();
  const { loginTrainer, trainers, mergedExercises } = useSessionData();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedExercise, setSelectedExercise] = useState<string>('');
  const [addExerciseModalData, setAddExerciseModalData] =
    useState<AddExerciseModalData | null>(null);
  const [editedExercise, setEditedExercise] = useState<Exercise | null>(null);

  const filteredExercises = useMemo(() => {
    if (!searchQuery) {
      return mergedExercises;
    }
    const searchQueryRegex = new RegExp(searchQuery.toLowerCase());
    return mergedExercises.filter(({ name }) =>
      searchQueryRegex.test(name.toLowerCase()),
    );
  }, [searchQuery, mergedExercises]);

  const canDeleteExercise = useCallback(
    (exercise: Exercise) => {
      if (!loginTrainer) {
        return false;
      }
      const exerciseIsPersonalized = Boolean(exercise.isPersonalized);
      if (exerciseIsPersonalized) {
        return exercise.trainerId === loginTrainer.id;
      }
      return isMotivisionTrainer(loginTrainer);
    },
    [loginTrainer],
  );

  if (!loginTrainer) {
    return (
      <EmptyState
        icon={<AccountCircleRoundedIcon />}
        text="No Exercises For Your Role"
      />
    );
  }

  const handleAddExercise = async (
    newExercise: WithOptionalId<Exercise>,
    options: AddExerciseModalData,
  ): Promise<boolean> => {
    const success = await createExercise(newExercise, options);
    if (success) {
      setAddExerciseModalData(null);
    }
    return success;
  };

  const handleEditExercise = async (
    oldExerciseData: Exercise,
    newExerciseData: Exercise,
  ): Promise<boolean> => {
    const success = await updateExercise(oldExerciseData, newExerciseData);
    if (!success) {
      return false;
    }
    setAddExerciseModalData(null);
    return true;
  };

  const renderAddExerciseDialog = () => {
    if (!addExerciseModalData) return null;
    return (
      <BaseExerciseDialog
        isPersonalized={!isMotivisionTrainer(loginTrainer)}
        title="Create An Exercise"
        trainerId={loginTrainer.id}
        originalExercise={addExerciseModalData.baseExercise}
        onSubmit={({ id, ...newExercise }: WithOptionalId<Exercise>) =>
          handleAddExercise(newExercise, addExerciseModalData)
        }
        onClose={() => setAddExerciseModalData(null)}
        isOpen={Boolean(addExerciseModalData)}
      />
    );
  };

  const renderEditExerciseDialog = () => {
    if (!editedExercise?.id) return null;
    return (
      <BaseExerciseDialog
        isPersonalized={
          !isMotivisionTrainer(loginTrainer) || !!editedExercise?.isPersonalized
        }
        title="Edit Exercise"
        trainerId={editedExercise.trainerId || loginTrainer.id}
        originalExercise={editedExercise}
        onSubmit={(updatedExercise) =>
          handleEditExercise(editedExercise, {
            ...updatedExercise,
            id: editedExercise.id,
          })
        }
        onClose={() => setEditedExercise(null)}
        isOpen={!!editedExercise}
      />
    );
  };

  const deleteExercise = async (exercise: Exercise) => {
    await deleteUserExercise(exercise.id);
  };

  const renderEmptyState = () => (
    <EmptyState
      icon={<AccountCircleRoundedIcon />}
      cta={{
        icon: <AddBoxOutlinedIcon color="primary" />,
        label: 'Add An Exercise',
        onClick: () => setAddExerciseModalData({ baseExercise: null }),
      }}
      text="No Exercises Have Been Added"
    />
  );

  if (!loginTrainer) {
    return renderEmptyState();
  }

  const renderContent = () => {
    return (
      <ListContent
        items={filteredExercises}
        listHeaderClassName={exerciseRowStyles.listGrid}
        EmptyStateComponent={renderEmptyState}
        titles={exerciseListTitles}
        ItemComponent={({ data }) => (
          <ExerciseRow
            exercise={data}
            deleteExercise={deleteExercise}
            allowDelete={canDeleteExercise(data)}
            openEditExerciseModal={() => setEditedExercise(data)}
            selected={selectedExercise === data.id}
            onSelect={() => setSelectedExercise(data.id)}
            key={data.id}
            exerciseTrainer={Object.values(trainers)
              .map(({ trainer }) => trainer)
              .find(({ id }) => data.trainerId === id)}
            addExerciseFromBaseExercise={(baseExercise) =>
              setAddExerciseModalData({
                baseExercise: baseExercise,
                isCreatingPersonalizedExercise: true,
              })
            }
          />
        )}
      />
    );
  };

  const getSearchPlaceholder = () => {
    if (!filteredExercises.length) return '';
    return 'Search for Exercises';
  };

  return (
    <ContentDrill
      title="Exercise List"
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add An Exercise',
          onClick: () => setAddExerciseModalData({ baseExercise: null }),
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      setSearchQuery={setSearchQuery}
      disableSearch={false}
    >
      {renderContent()}
      {renderAddExerciseDialog()}
      {renderEditExerciseDialog()}
    </ContentDrill>
  );
};

export default TrainerExercisesContent;
